import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/App";
import { setPageMeta } from "@/lib/pageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Star, CheckCircle2 } from "lucide-react";

const MAX = 600;

const Stars = ({ value, onChange }) => (
  <div className="flex gap-1.5" data-testid="review-stars">
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type="button" onClick={() => onChange(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`} className="p-0.5 transition-transform hover:scale-110" data-testid={`review-star-${n}`}>
        <Star className={`w-8 h-8 ${n <= value ? "fill-[#FF7A00] text-[#FF7A00]" : "text-stone-300"}`} />
      </button>
    ))}
  </div>
);

export default function LeaveReview() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ rating: 0, story: "", display_name: "", publish_consent: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    setPageMeta({ title: "Share your PlateMind experience", description: "Tell us how your nutrition care went. It takes about two minutes." });
    fetch(`${API}/public/review/${token}`).then(async r => {
      const d = await r.json();
      if (!r.ok) { setError(d.detail || "This link is not valid."); return; }
      setInfo(d); setForm(f => ({ ...f, display_name: d.suggested_display_name || "" }));
    }).catch(() => setError("Connection error. Please try again."));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) return setFormError("Please choose a star rating.");
    if (form.story.trim().length < 10) return setFormError("Please write a little more about your experience.");
    if (!form.display_name.trim()) return setFormError("Please enter the name you'd like shown.");
    setFormError(""); setBusy(true);
    try {
      const r = await fetch(`${API}/public/review/${token}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      const d = await r.json();
      r.ok ? setDone(true) : setFormError(d.detail || "Could not submit. Please try again.");
    } catch { setFormError("Connection error. Please try again."); } finally { setBusy(false); }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF8F2] to-white px-5 py-10" data-testid="review-page">
      <div className="max-w-lg mx-auto">
        <img src="/platemind-logo-new.png" alt="PlateMind" className="h-8 w-auto mb-8" />
        <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-6 sm:p-8">{children}</div>
      </div>
    </div>
  );

  if (error) return shell(<p className="text-sm text-stone-700" data-testid="review-error">{error}</p>);
  if (!info) return shell(<Loader2 className="w-6 h-6 animate-spin text-[#FF7A00]" />);
  if (done) return shell(
    <div className="text-center space-y-3" data-testid="review-success">
      <CheckCircle2 className="w-10 h-10 text-green-600 mx-auto" />
      <h1 className="text-xl font-bold text-stone-900">Thank you{info.first_name ? `, ${info.first_name}` : ""}!</h1>
      <p className="text-sm text-stone-500">Your feedback helps us improve our care and helps others take the first step.</p>
    </div>
  );

  return shell(
    <form onSubmit={submit} className="space-y-6" data-testid="review-form">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-stone-900">How was your PlateMind experience?</h1>
        <p className="mt-1.5 text-sm text-stone-500">{info.first_name ? `Hi ${info.first_name}, w` : "W"}e'd love to hear how your nutrition care went. It takes about two minutes.</p>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold text-stone-800">Your rating</label>
        <Stars value={form.rating} onChange={v => setForm({ ...form, rating: v })} />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold text-stone-800">Your story</label>
        <Textarea value={form.story} maxLength={MAX} rows={6} onChange={e => setForm({ ...form, story: e.target.value })}
          placeholder="What changed for you? What did you find most helpful?" className="rounded-xl" data-testid="review-story" />
        <p className="text-[11px] text-stone-400 text-right" data-testid="review-char-count">{form.story.length}/{MAX}</p>
      </div>
      <div className="space-y-2">
        <label className="text-sm font-semibold text-stone-800">Name to show</label>
        <Input value={form.display_name} maxLength={60} onChange={e => setForm({ ...form, display_name: e.target.value })} placeholder="e.g. Amaka O." className="rounded-xl" data-testid="review-display-name" />
        <p className="text-[11px] text-stone-400">We suggest your first name and last initial.</p>
      </div>
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={form.publish_consent} onChange={e => setForm({ ...form, publish_consent: e.target.checked })} className="mt-1 accent-[#FF7A00]" data-testid="review-consent" />
        <span className="text-xs text-stone-600 leading-relaxed">I allow PlateMind to share my story and the name above on its website. PlateMind may correct minor spelling mistakes.</span>
      </label>
      {formError && <p className="text-sm text-red-600" data-testid="review-form-error">{formError}</p>}
      <Button type="submit" disabled={busy} className="w-full bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl h-12 text-base font-semibold" data-testid="review-submit">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Submit my review
      </Button>
    </form>
  );
}
